import React, { useState } from 'react'
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Image } from 'react-native'
import {
  useNavigation,
  useRoute,
  useTheme,
  type NavigationProp,
  type ParamListBase,
} from '@react-navigation/native'
import { Icon } from '../../../components/atoms/Icon'
import { Theme } from '@theme'
import { getCommonStyles } from '@utils'
import ContactsList from '../components/ContactsList'
import { Screen } from '../../../components/templates/Screen'
import { useCreateCategory, useScreen, useKeyboardVisible } from '@hooks'
import { BackButtonWeb } from '../../../components/molecules/BackButton'
import { Header } from '../../../components/molecules/Header'
import { ImageUploadBase } from '../../../components/molecules/ImageUpload'
import CloseButton from '../components/CloseButton'
import { useLanguage } from '@context'

export const TitleCategory: React.FC<{
  title: string
  placeholder?: string
  setTitle: (title: string) => void
}> = ({ title, placeholder, setTitle }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  return (
    <View style={styles.titleContainer}>
      <TextInput
        style={styles.titleInput}
        placeholder={placeholder || t('categoriesScreen.categoryTitlePlaceholder')}
        value={title}
        onChangeText={setTitle}
        autoCapitalize="none"
        placeholderTextColor={theme.colors.greyBlue}
      />
      <Icon name="edit" size={20} color={theme.colors.greyBlue} />
    </View>
  )
}

export const PhotoCategory: React.FC<{
  photo?: string
  isSubcategory?: boolean
  isEdit?: boolean
  onAddPhoto: (uri: string) => void
}> = ({ photo, isSubcategory, isEdit = false, onAddPhoto }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  const styles = getStyle(theme)

  return (
    <ImageUploadBase onImageSelect={onAddPhoto} style={styles.photoCard}>
      {photo ? (
        <Image source={{ uri: photo }} style={styles.photo} resizeMode="contain" />
      ) : (
        <View style={[styles.photoPlaceholder, isMobile && !isEdit && styles.photoPlaceholderMobile]}>
          <Icon name="upload" size={40} color={theme.colors.greyBlue} />
          <Text style={styles.photoText}>
            {isSubcategory
              ? t('categoriesScreen.subcategoryPhoto')
              : t('categoriesScreen.categoryPhoto')}
          </Text>
        </View>
      )}
      {isEdit && (
        <View style={styles.editPhotoButton}>
          <Icon name="editPen" size={20} color={theme.colors.greyBlue} />
        </View>
      )}
    </ImageUploadBase>
  )
}

const AddCategory: React.FC = () => {
  const navigation = useNavigation<NavigationProp<ParamListBase>>()
  const route = useRoute<any>()
  const categoryId = route.params?.categoryId
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  const keyboardVisible = useKeyboardVisible()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)

  const { mutate: createCategory } = useCreateCategory()

  const [categoryTitle, setCategoryTitle] = useState('')
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([])
  const [photo, setPhoto] = useState<string | undefined>(undefined)
  // While searching contacts on mobile, collapse the photo/title block so the
  // search field and the contacts list stay visible above the keyboard.
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const goBackToList = () => {
    navigation.navigate('Menu', {
      screen: 'Categories',
      params: {
        screen: 'CategoriesList',
      },
    })
  }

  // Fall back to the list when there's no screen to go back to (e.g. the page
  // was opened directly / after a reload), otherwise GO_BACK silently fails
  // and the user stays stuck on the form.
  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      goBackToList()
    }
  }

  const handleSave = () => {
    if (categoryId) {
      //TODO: Add subcategory
      return
    }
    // Navigate only once the create succeeds: onSuccess invalidates the
    // 'categories' query, so landing on the list after it guarantees the
    // freshly created category is visible. Firing navigation synchronously
    // would race the cache invalidation and hide the new category.
    createCategory(
      {
        name: categoryTitle,
        avatar: photo ? { uri: photo } : undefined,
        contactAccountIds: selectedContactIds,
      },
      { onSuccess: () => goBackToList() },
    )
  }

  const handleAddPhoto = (uri: string) => {
    setPhoto(uri)
  }

  return (
    <Screen>
      {isMobile && (
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack}>
            <Icon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {categoryId ? t('categoriesScreen.addSubcategory') : t('categoriesScreen.addCategory')}
          </Text>
          <View style={styles.placeholder} />
        </View>
      )}
      {!isMobile && (
        <BackButtonWeb title={t('categoriesScreen.backToList')} onPress={goBackToList} />
      )}
      <View
        style={[
          styles.content,
          isMobile && commonStyles.paddingHorizontal,
          !isMobile && commonStyles.searchContainerNonMobileWithoutPaddingTop,
          !isMobile && commonStyles.alignStart,
          !isMobile && commonStyles.gapXxxl,
        ]}
      >
        {!(isMobile && isSearchFocused && keyboardVisible) && (
          <View>
            {!isMobile && <Header title={t('categoriesScreen.newCategory')} />}
            <View style={isMobile ? commonStyles.paddingTop : styles.infoContainer}>
              {/* Photo de la catégorie */}
              <PhotoCategory isSubcategory={categoryId} photo={photo} onAddPhoto={handleAddPhoto} />
              {/* Titre de la catégorie */}
              <TitleCategory
                title={categoryTitle}
                setTitle={setCategoryTitle}
                placeholder={categoryId && t('categoriesScreen.subcategoryTitlePlaceholder')}
              />
            </View>
          </View>
        )}
        {/* alignSelf stretch : le conteneur est aligné en haut sur ordinateur, sans lui la liste
            n'est pas bornée en hauteur et ne défile pas. */}
        <View style={styles.contactsColumn}>
          <ContactsList
            selectedContactIds={selectedContactIds}
            setSelectedContactIds={setSelectedContactIds}
            onSearchFocusChange={setIsSearchFocused}
          />
        </View>
      </View>
      {/* Bouton Sauvegarder */}
      <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
        {!isMobile && <CloseButton onPress={handleBack} />}
        <TouchableOpacity
          style={[styles.saveButton, !categoryTitle.trim() && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!categoryTitle.trim()}
        >
          <Text style={styles.saveButtonText}>{t('common.save')}</Text>
        </TouchableOpacity>
      </View>
    </Screen>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    headerTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: 'bold',
      color: theme.colors.text,
    },
    placeholder: {
      width: 30,
    },
    content: {
      flex: 1,
    },
    contactsColumn: {
      flex: 1,
      alignSelf: 'stretch',
      minHeight: 0,
    },
    infoContainer: {
      margin: theme.spacing.md,
    },
    photoCard: {
      borderRadius: 20,
      marginBottom: 16,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    editPhotoButton: {
      position: 'absolute',
      top: 12,
      right: 12,
      backgroundColor: 'rgba(255,255,255,0.9)',
      borderRadius: 20,
      padding: 8,
      pointerEvents: 'none',
    },
    photo: {
      width: '100%',
      height: 200,
    },
    photoPlaceholder: {
      height: 200,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background,
    },
    photoText: {
      fontSize: 16,
      color: theme.colors.greyBlue,
      marginTop: 8,
    },
    // Phone: the empty photo card is shorter so that the contacts list under it
    // is tall enough to be used (it showed two and a half people).
    photoPlaceholderMobile: {
      height: 110,
    },
    titleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 25,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    titleInput: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    addContactButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    },
    addContactText: {
      marginLeft: 12,
      flex: 1,
    },
    addContactTitle: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: '500',
    },
    addContactSubtitle: {
      fontSize: 13,
      color: theme.colors.greyBlue,
      marginTop: 2,
    },
    selectedContactsContainer: {
      marginBottom: 16,
    },
    selectedContactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 12,
      marginBottom: 8,
    },
    selectedContactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 12,
    },
    selectedContactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    removeButton: {
      padding: 4,
    },
    filtersContainer: {
      flexDirection: 'row',
      marginBottom: 16,
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    searchContainer: {
      marginBottom: 16,
    },
    searchInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 12,
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    searchIcon: {
      marginRight: 8,
    },
    searchInput: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    activeFilter: {
      backgroundColor: '#E3F2FD',
      borderColor: theme.colors.primary,
    },
    filterText: {
      fontSize: 14,
      color: theme.colors.greyBlue,
      marginLeft: 4,
    },
    activeFilterText: {
      color: theme.colors.primary,
    },
    hashtagText: {
      fontSize: 16,
      color: theme.colors.greyBlue,
      fontWeight: 'bold',
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.white,
    },
    saveButton: {
      backgroundColor: theme.colors.white,
      borderRadius: 25,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xxxl,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    saveButtonDisabled: {
      opacity: 0.5,
    },
    saveButtonText: {
      fontSize: theme.fontSize.small,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      justifyContent: 'flex-end',
    },
    modalContainer: {
      backgroundColor: theme.colors.white,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      minHeight: 200,
      maxHeight: '80%',
    },
    handle: {
      width: 36,
      height: 4,
      backgroundColor: theme.colors.lightGrey,
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 8,
      marginBottom: 8,
    },
    modalHeader: {
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
    },
    modalSubtitle: {
      fontSize: 14,
      color: theme.colors.greyBlue,
      padding: 20,
      paddingTop: 10,
    },
    modalContactsList: {
      flex: 1,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.lightGrey,
    },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      marginRight: 12,
    },
    contactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    selectionBar: {
      height: 40,
      width: 6,
      borderRadius: 3,
      backgroundColor: 'transparent',
      marginLeft: theme.spacing.md,
    },
    selectionBarActive: {
      backgroundColor: theme.colors.primary,
    },
    overlayTouchable: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    modalFooter: {
      padding: 20,
      borderTopWidth: 0.5,
      borderTopColor: theme.colors.lightGrey,
    },
    modalSaveButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalSaveButtonText: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.white,
    },
    safeArea: {
      flex: 1,
    },
    contactsListContainer: {
      marginTop: 0,
    },
  })

export default AddCategory
